import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import './OddsViewer.css';

const OddsViewer = () => {
  const [marketId, setMarketId] = useState('');
  const [socket, setSocket] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [bookmakerOdds, setBookmakerOdds] = useState(null);
  const [sessionOdds, setSessionOdds] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [liveScore, setLiveScore] = useState(null);
  const [ballRunning, setBallRunning] = useState(false);

  useEffect(() => {
    if (!marketId) return;

  const newSocket = io({
    transports: ['websocket', 'polling']
  });

    setSocket(newSocket);
    setConnectionStatus('connecting');

    newSocket.on('connect', () => {
      console.log('Socket connected');
      setConnectionStatus('connected');
      newSocket.emit('join_match', marketId);
      newSocket.emit('getOddData', { eventId: marketId });
      newSocket.emit('liveScore', { eventId: marketId });
    });

    newSocket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setConnectionStatus('error');
    });

    // Handle odds update
    newSocket.on('eventGetLiveEventsFancyData', (data) => {
      console.log('Odds update received:', data);
      
      if (data.body?.cricket?.[0]) {
        setBookmakerOdds(data.body.cricket[0]);
      }
      
      if (data.body?.session?.[0]?.value?.session) {
        setSessionOdds(data.body.session[0].value.session);
      }
    });

    // Handle live score updates
    newSocket.on('liveScore', (data) => {
      console.log('Live score:', data);
      setLiveScore(data.data);
      
      // Check if ball is running
      if (data.data.spnballrunningstatus === "Ball Running") {
        setBallRunning(true);
        setTimeout(() => setBallRunning(false), 3000);
      }
    });

    // Handle ball running status
    newSocket.on('ball_update', (data) => {
      setBallRunning(true);
      setTimeout(() => setBallRunning(false), 3000);
    });

    // Handle DRS status
    newSocket.on('drs_request', (data) => {
      setBallRunning(true);
      setTimeout(() => setBallRunning(false), 3000);
    });

    newSocket.on('disconnect', () => {
      setConnectionStatus('disconnected');
    });

    return () => {
      newSocket.disconnect();
    };
  }, [marketId]);

  const parseScore = (scoreString) => {
    if (!scoreString) return { runs: 0, wickets: 0, overs: 0, balls: 0 };
    const match = scoreString.match(/(\d+)-(\d+)\s*\((\d+)\.(\d+)\)/);
    if (match) {
      return {
        runs: parseInt(match[1]),
        wickets: parseInt(match[2]),
        overs: parseInt(match[3]),
        balls: parseInt(match[4])
      };
    }
    return { runs: 0, wickets: 0, overs: 0, balls: 0 };
  };

  const getStatusText = (gameStatus, active) => {
    if (ballRunning) return "Ball Running";
    if (gameStatus === "SUSPENDED" || active === "SUSPENDED") return "SUSPENDED";
    return "ACTIVE";
  };

  const getStatusClass = (gameStatus, active) => {
    if (ballRunning) return "status-ball-running";
    if (gameStatus === "SUSPENDED" || active === "SUSPENDED") return "status-suspended";
    return "status-active";
  };

  if (!marketId) {
    return (
      <div className="odds-viewer">
        <div className="viewer-header">
          <h2>Live Odds Viewer</h2>
          <div className="market-selector">
            <input
              type="text"
              placeholder="Enter Market ID"
              value={marketId}
              onChange={(e) => setMarketId(e.target.value)}
            />
            <p className="hint">Enter Market ID to view live odds</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="odds-viewer">
      <div className="viewer-header">
        <h2>Live Odds Viewer</h2>
        <div className="market-info">
          <span>Market ID: {marketId}</span>
          <span className={`connection-status ${connectionStatus}`}>
            {connectionStatus === 'connected' ? '🟢 Connected' : '🔴 Disconnected'}
          </span>
          {ballRunning && <span className="ball-running">🏏 Ball Running...</span>}
        </div>
      </div>

      {/* Live Score Section */}
      {liveScore && (
        <div className="live-score-section">
          <h3>Live Score</h3>
          <div className="live-score">
            <div className="team-score">
              <span className="team-name">{liveScore.spnnation1}</span>
              <span className="score">{liveScore.score1}</span>
              <span className="run-rate">RR: {liveScore.spnrunrate1}</span>
            </div>
            <div className="vs">vs</div>
            <div className="team-score">
              <span className="team-name">{liveScore.spnnation2}</span>
              <span className="score">{liveScore.score2}</span>
              <span className="run-rate">RR: {liveScore.spnrunrate2}</span>
            </div>
          </div>
          {liveScore.spnmessage && (
            <div className="match-message">{liveScore.spnmessage}</div>
          )}
          <div className="recent-balls">
            {liveScore.balls?.map((ball, idx) => (
              <span key={idx} className={`ball ${ball === 'W' ? 'wicket' : ''} ${ball === '4' || ball === '6' ? 'boundary' : ''}`}>
                {ball || '•'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Bookmaker Odds Section */}
      {bookmakerOdds && (
        <div className="odds-section">
          <h3>Match Odds (Bookmaker)</h3>
          <div className="bookmaker-odds">
            {bookmakerOdds.runners?.map((runner, idx) => (
              <div key={idx} className="odd-card">
                <div className="team-name">{runner.name}</div>
                <div className="odds-container">
                  <div className="back-odds">
                    <span className="label">BACK</span>
                    <div className="odds-values">
                      {runner.back?.map((odd, i) => (
                        <span key={i} className="odd-value">{odd.price.toFixed(2)}</span>
                      ))}
                    </div>
                  </div>
                  <div className="lay-odds">
                    <span className="label">LAY</span>
                    <div className="odds-values">
                      {runner.lay?.map((odd, i) => (
                        <span key={i} className="odd-value">{odd.price.toFixed(2)}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="market-status">
            Status: <span className={bookmakerOdds.status === 'OPEN' ? 'status-open' : 'status-suspended'}>
              {bookmakerOdds.status}
            </span>
          </div>
        </div>
      )}

      {/* Session Odds Section - Over/Under */}
      {sessionOdds.length > 0 && (
        <div className="odds-section">
          <h3>Session Odds (Over/Under)</h3>
          <div className="session-odds-grid">
            {sessionOdds.map((session, idx) => {
              const isOver = session.RunnerName.includes('Over');
              const statusText = getStatusText(session.GameStatus, session.Active);
              const statusClass = getStatusClass(session.GameStatus, session.Active);
              
              // Group by marketId (same over)
              const overUnderPair = sessionOdds.filter(s => s.marketId === session.marketId);
              if (overUnderPair.length === 2 && idx % 2 === 0) {
                const over = overUnderPair[0];
                const under = overUnderPair[1];
                const overStatus = getStatusText(over.GameStatus, over.Active);
                const underStatus = getStatusText(under.GameStatus, under.Active);
                const isSuspended = overStatus === 'SUSPENDED' || underStatus === 'SUSPENDED';
                
                return (
                  <div key={session.marketId} className="session-pair">
                    <div className="over-title">{session.RunnerName.split(' - ')[0]}</div>
                    <div className="over-under-container">
                      <div className={`over-option ${isSuspended ? 'suspended' : ''}`}>
                        <div className="option-name">YES (Over)</div>
                        <div className="option-odds">
                          <span className="back">Back: {over.BackPrice1 ? (over.BackPrice1 / 100).toFixed(2) : '0'}</span>
                          <span className="lay">Lay: {over.LayPrice1 ? (over.LayPrice1 / 100).toFixed(2) : '0'}</span>
                        </div>
                        <div className={`status-badge ${statusClass}`}>{overStatus}</div>
                      </div>
                      <div className={`under-option ${isSuspended ? 'suspended' : ''}`}>
                        <div className="option-name">NO (Under)</div>
                        <div className="option-odds">
                          <span className="back">Back: {under.BackPrice1 ? (under.BackPrice1 / 100).toFixed(2) : '0'}</span>
                          <span className="lay">Lay: {under.LayPrice1 ? (under.LayPrice1 / 100).toFixed(2) : '0'}</span>
                        </div>
                        <div className={`status-badge ${statusClass}`}>{underStatus}</div>
                      </div>
                    </div>
                    {session.Remark && <div className="remark">{session.Remark}</div>}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}

      {(!bookmakerOdds && sessionOdds.length === 0) && (
        <div className="loading">
          <p>Waiting for odds data...</p>
          <p className="hint">Make sure the match exists and is live</p>
        </div>
      )}
    </div>
  );
};

export default OddsViewer;